"use client";

import type { ReactNode } from "react";
import { defaultAvatarRenderer } from "../renderers";
import type { AvatarRenderer, BaseInteraction, FacepileSize } from "../types";

interface MobileAdapterProps<T extends BaseInteraction> {
	item: T;
	size: FacepileSize;
	renderAvatar?: AvatarRenderer<T>;
	renderInteraction?: (item: T) => ReactNode;
	onItemPress?: (item: T) => void;
}

/**
 * Mobile-specific adapter that handles press interactions
 * No hover cards on mobile, but could show bottom sheet on press
 */
export function MobileAdapter<T extends BaseInteraction>({
	item,
	size,
	renderAvatar = defaultAvatarRenderer,
	renderInteraction,
	onItemPress,
}: MobileAdapterProps<T>) {
	// Base avatar element
	const avatarElement = renderAvatar(item, size);

	// For mobile, wrap in a pressable element
	// Note: This is a simplified version for web compatibility
	// In a real React Native app, this would use Pressable or TouchableOpacity
	return (
		<div
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				onItemPress?.(item);
			}}
			className="cursor-pointer"
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					e.stopPropagation();
					onItemPress?.(item);
				}
			}}
		>
			{avatarElement}
		</div>
	);
}
